import { Money } from '@betterment/core';

import { timeRangeBuckets } from './timeRange';

type EmptyChartDatumForDate = {
  dateTime: Date;
};

type ReturnsType = {
  __typename: string;
  amount: Money | null;
  percentage: number | null;
};

export type PricePointType = {
  __typename: string;
  dateTime: Date;
  amount: Money | null;
  returns: ReturnsType;
};

type IntradayPricePointsForTimeRangeType = {
  startTime: Date;
  endTime: Date;
  pricePoints: PricePointType[];
};

const emptyChartDatumForDate = ({ dateTime }: EmptyChartDatumForDate) => {
  return {
    __typename: 'FinancialSecurityPricePerformancePricePoint' as const,
    dateTime,
    amount: Money(0),
    returns: {
      __typename: 'FinancialSecurityPriceReturns' as const,
      amount: Money(0),
      percentage: 0,
    },
  } as PricePointType;
};

type EmptyChartDataForDate = {
  date: Date;
};

export const emptyChartDataForDate = ({ date }: EmptyChartDataForDate): PricePointType[] => {
  return [emptyChartDatumForDate({ dateTime: date })];
};

export const intradayPricePointsForTimeRange = ({
  startTime,
  endTime,
  pricePoints,
}: IntradayPricePointsForTimeRangeType): PricePointType[] => {
  const buckets = timeRangeBuckets({
    startTime,
    endTime,
  });

  const firstAmountCents = pricePoints[0]?.amount?.cents ?? null;

  return buckets.map(bucket => {
    const filteredPricePoints = pricePoints.filter(pricePoint => {
      const pointTime = pricePoint.dateTime.getTime();
      return pointTime >= bucket.startAt.getTime() && pointTime < bucket.endAt.getTime();
    });

    const amount =
      filteredPricePoints.length > 0
        ? filteredPricePoints.reduce((sum, point) => sum + (point.amount?.cents ?? 0), 0) / filteredPricePoints.length
        : null;

    const dateTime = bucket.isLast ? bucket.endAt : bucket.startAt;

    let returnAmountCents = null;
    let returnPercent = null;

    if (amount !== null && firstAmountCents !== null) {
      returnAmountCents = amount - firstAmountCents;
      returnPercent = firstAmountCents > 0 ? (returnAmountCents / firstAmountCents) * 100 : null;
    }

    const returnPercentage = returnPercent !== null ? Math.round(returnPercent * 100) / 100 : null;

    return {
      __typename: 'FinancialSecurityPricePerformancePricePoint' as const,
      dateTime,
      amount: amount !== null ? Money(amount) : null,
      returns: {
        __typename: 'FinancialSecurityPriceReturns' as const,
        amount: returnAmountCents !== null ? Money(returnAmountCents) : null,
        percentage: returnPercentage,
      },
    };
  });
};
